import React, { useState, useEffect } from 'react';
import { Box, BoxProps, useTheme, useMediaQuery } from '@mui/material';

interface LogoProps extends Omit<BoxProps, 'component'> {
  /**
   * Altura da logo em pixels. Largura será ajustada automaticamente mantendo a proporção.
   * @default 24
   */
  height?: number;
  /**
   * Variante da logo a ser exibida.
   * - 'logo': Logo completa com texto
   * - 'icon': Apenas o ícone (icone.png)
   * - 'auto': Automático baseado no tamanho da tela (icon em mobile, logo em desktop)
   * @default 'auto'
   */
  variant?: 'logo' | 'icon' | 'auto';
  /**
   * Cor da logo baseado no fundo onde será exibida.
   * - 'white': Logo branca (logo-white.png) - para fundos escuros
   * - 'gray': Logo cinza (logo-gray.png) - para fundos claros
   * - 'default': Logo original (logo.png) - cor padrão
   * @default 'default'
   */
  color?: 'white' | 'gray' | 'default';
  /**
   * Se true, a logo será clicável e redirecionará para a home
   * @default false
   */
  clickable?: boolean;
  /**
   * Callback opcional quando a logo for clicada
   */
  onClick?: () => void;
}

/**
 * Componente Logo do V-Condo
 *
 * Utiliza logo_vcondo.svg (logo completa) ou logo_vc.png (apenas ícone)
 * da pasta public/images. Pode ser usado como favicon, no cabeçalho, landing page, etc.
 */
const Logo: React.FC<LogoProps> = ({ 
  height = 24,
  variant = 'auto',
  color = 'default',
  clickable = false,
  onClick,
  sx,
  ...boxProps 
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [imageError, setImageError] = useState(false);

  // Determinar qual variante usar
  let imageVariant: 'logo' | 'icon';
  if (variant === 'auto') {
    imageVariant = isMobile ? 'icon' : 'logo';
  } else {
    imageVariant = variant;
  }

  // Determinar arquivo baseado na variante e cor
  let imageSrc: string;
  let altText: string;
  
  if (imageVariant === 'icon') {
    imageSrc = '/images/logo_vc.png';
    altText = 'V-Condo Icon';
  } else {
    switch (color) {
      case 'white':
        imageSrc = '/images/logo_vcondo.svg';
        altText = 'V-Condo Logo (White)';
        break;
      case 'gray':
      case 'default':
      default:
        imageSrc = '/images/logo_vcondo.svg';
        altText = 'V-Condo Logo';
        break;
    }
  }

  const logoStyle = {
    height: `${height}px`,
    width: 'auto',
    display: 'block',
    ...(clickable && {
      cursor: 'pointer',
    }),
  };

  const handleClick = () => {
    if (clickable) {
      if (onClick) {
        onClick();
      }
    }
  };

  const handleImageError = () => {
    if (!imageError && imageVariant === 'logo') {
      setImageError(true);
    }
  };

  useEffect(() => {
    setImageError(false);
  }, [imageSrc, imageVariant]);

  const finalSrc = imageError && imageVariant === 'logo'
    ? '/images/logo_vc.png'
    : imageSrc;

  return (
    <Box
      component="img"
      src={finalSrc}
      alt={altText}
      onError={handleImageError}
      onLoad={() => setImageError(false)}
      sx={{
        ...logoStyle,
        // Em fundos escuros (color="white"), aplicar filtro para logo ficar visível
        ...(color === 'white' && {
          filter: 'brightness(0) invert(1)'
        }),
        ...sx,
      }}
      onClick={clickable ? handleClick : undefined}
      {...boxProps}
    />
  );
};

export default Logo;

